import * as _ from "lodash";
import { AppRegistry } from "../../../apps/AppRegistry";
import { AppCollection } from "../../../collections/Apps";
import { BrandCollection } from "../../../collections/Brands";
import { DashboardCategoryCollection } from "../../../collections/DashboardCategories";
import { DashboardChannelCollection } from "../../../collections/DashboardChannels";
import { DashboardWidgetCollection } from "../../../collections/DashboardWidgets";
import { DashboardCollection } from "../../../collections/Dashboards";
import { InstallCollection } from "../../../collections/Installs";
import { ModuleCollection } from "../../../collections/Modules";
import { OrganizationCollection } from "../../../collections/Organizations";
import { PermissionCollection } from "../../../collections/Permissions";
import { User } from "../../../models/User";
import { BaseController } from "../../_BaseViews";
import { ModuleLayoutWidget } from "../../dashboard/widgets/layout/ModuleLayoutWidget";
import { ModifyDashboardWidgetModal } from "../../modals/dashboard/modify_widgets/ModifyDashboardWidgetModal";
import { AggregateTableWidgetView } from "../../reports/widgets/aggregatetablewidget/AggregateTableWidgetView";
import { CSVDataWidgetView } from "../../reports/widgets/csvdatawidget/CSVDataWidgetView";
import { DateTimeWidgetView } from "../../reports/widgets/datetimewidget/DateTimeWidgetView";
import { SignatureWidgetView } from "../../reports/widgets/signaturewidget/SignatureWidgetView";
import { TextWidgetView } from "../../reports/widgets/textwidget/TextWidgetView";
import { TitleWidgetView } from "../../reports/widgets/titlewidget/TitleWidgetView";
import { TotalWidgetView } from "../../reports/widgets/totalwidget/TotalWidgetView";
import { ChartWorkerManager } from "./ChartWorkerManager";
import { ChartView } from "./chart/ChartView";
import { IAQWidget } from "./iaq/IAQWidget";
import { LobbyViewWidget } from "./lobbyview/LobbyViewWidget";
import { MultiGaugeWidget } from "./multigauge/MultiGauge";
import { SensitivityWidget } from "./sensitivity/SensitivityWidget";
import { SimpleWidget } from "./simplewidget/SimpleWidget";
import { TableWidgetView } from "./table/TableWidgetView";
import { IframeWidget } from "./iframe/IframeWidget";

interface DashboardWidgetManagerOptions extends Backbone.ViewOptions<Backbone.Model> {
    appRegistry: AppRegistry,
    appCollection: AppCollection,
    brandCollection: BrandCollection,
    moduleCollections?: { [key: number]: ModuleCollection };
    installCollection: InstallCollection;
    permissionCollection: PermissionCollection;
    organizationCollection: OrganizationCollection;
    dashboardCollection: DashboardCollection;
    dashboardChannelCollection: DashboardChannelCollection;
    dashboardCategoryCollection: DashboardCategoryCollection;
    dashboardWidgetCollection: DashboardWidgetCollection;
    dashboardId: number;
    currentUser: User;
    report: Backbone.Model;
    validWidgets: number[];
}


export class DashboardWidgetManager extends BaseController {

    options: DashboardWidgetManagerOptions;
    chartWorkerManager: ChartWorkerManager;
    currentFilter: string = null;

    private modalView = null;

    // Maps widgetIds to widget views
    private widgetViews = {};

    constructor(options: DashboardWidgetManagerOptions) {
        super(options);

        this.chartWorkerManager = new ChartWorkerManager({
            dashboardChannelCollection: this.options.dashboardChannelCollection
        });

        this.listenTo(window['App'].Bus, 'dashboard:filter:notify', (filterValue) => {
            this.currentFilter = filterValue;
        });
    }

    getViewFromWidgetModel(widgetModel, viewOptions, parentView) {
        var deferred = $.Deferred();
        var widgetId = widgetModel.get('id');

        var widgetChannels = this.options.dashboardChannelCollection.where({
            dashboard_widget_id: widgetId
        });
        // Make sure the widget is not editable if the widget contains channels from
        // installs for which the user does not have view permission
        var editable = _.every(widgetChannels.map((c) => {
            return this.options.permissionCollection.canViewInstall(c.get('install_id'))
        }));

        var options = {
            dashboardWidgetCollection: this.options.dashboardWidgetCollection,
            dashboardChannelCollection: this.options.dashboardChannelCollection,
            permissionCollection: this.options.permissionCollection,
            widget: widgetModel,
            dashboardId: widgetModel.get('dashboard_id'),
            categoryId: widgetModel.get('dashboard_category_id'),
            widgetId: widgetId,
            parentView: parentView,
            currentUser: this.options.currentUser,
            manager: this,
            chartWorkerManager: this.chartWorkerManager,
            editable: editable,
            cloneable: true,
            movable: true,
            deletable: true,
            report: this.options.report
        };
        var widgetMap = {
            channels: SimpleWidget,
            chart: ChartView,
            default_chart: ChartView,
            iaq: IAQWidget,
            table: TableWidgetView,
            text: TextWidgetView,
            title: TitleWidgetView,
            signature: SignatureWidgetView,
            date_time: DateTimeWidgetView,
            total: TotalWidgetView,
            module_layout: ModuleLayoutWidget,
            multi_gauge: MultiGaugeWidget,
            sensitivity: SensitivityWidget,
            lobbyview: LobbyViewWidget,
            aggregate_table: AggregateTableWidgetView,
            csv_data: CSVDataWidgetView,
            iframe: IframeWidget
        };
        var widgetType = widgetModel.get('type');
        if (_.has(widgetMap, widgetType)) {
            setTimeout(() => {
                deferred.resolve(this.widgetViews[widgetId] = new widgetMap[widgetType](options));
            }, 0);
        } else {
            widgetType = widgetModel.get('app_type');
            this.options.appRegistry.getInstance(widgetType, (err, app) => {
                $.when(app.getWidgetView(options)).done((widgetView) => {
                    deferred.resolve(this.widgetViews[widgetId] = widgetView);
                });
            });
        }
        return deferred;
    }

    createModifyDashboardWidgetModal(widget) {
        if (this.modalView) this.modalView.remove();
        this.modalView = new ModifyDashboardWidgetModal({
            appRegistry: this.options.appRegistry,
            appCollection: this.options.appCollection,
            brandCollection: this.options.brandCollection,
            installCollection: this.options.installCollection,
            permissionCollection: this.options.permissionCollection,
            organizationCollection: this.options.organizationCollection,
            dashboardCollection: this.options.dashboardCollection,
            dashboardChannelCollection: this.options.dashboardChannelCollection,
            dashboardCategoryCollection: this.options.dashboardCategoryCollection,
            dashboardWidgetCollection: this.options.dashboardWidgetCollection,
            dashboardId: this.options.dashboardId,
            moduleCollections: this.options.moduleCollections,
            currentUser: this.options.currentUser,
            widget: widget,
            categoryId: widget ? widget.get('dashboard_category_id') : null,
            modelId: null,
            validWidgets: this.options.validWidgets,
            report: this.options.report
        });
        $('.modals').append(this.modalView.render().el);
        this.modalView.show();
    }

    getWidgetViewFromWidgetId(id: number) {
        return this.widgetViews[id];
    }

    removeWidgetView(id) {
        delete this.widgetViews[id];
    }

    remove() {
        this.chartWorkerManager.reset();
        if (this.modalView) this.modalView.remove();
        super.remove();
        return this;
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/dashboard/widgets/DashboardWidgetManager.ts